import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { animationIcons } from '../../utils/imagePath';
import {
  addAnimationObject,
  removeAnimationObject,
  updateAnimationObject,
} from '../../redux/Slice/animationSlice';

const AnimationLeftPanel = ({
  isMinimized,
  setIsMinimized,
  currentTimeIndicator,
  onDrop,
  onDragOver,
  selectedObject,
  onObjectSelect,
  onDeleteAnimationObject,
  timelineEffects,
}) => {
  const dispatch = useDispatch();
  const { animationObjects } = useSelector((state) => state.animation);
  const { physicsObjects } = useSelector((state) => state.physics);

  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      if (data.id || data.name) {
        const duplicateMatch = data.id
          ? animationObjects.some((obj) => obj.id === data.id)
          : animationObjects.some((obj) => obj.name === data.name);

        if (duplicateMatch) {
          toast.error('Object is already in the Animation Panel', {
            style: { background: '#1d1e21', color: '#fff' },
          });
          return;
        }

        // Physics-Animation conflict check
        const hasPhysics = physicsObjects.some(
          (p) => p.objectId === data.id || p.objectId === data.name
        );
        if (hasPhysics) {
          toast.error(
            "Physics and animation can't be used together. Remove physics to continue.",
            { style: { background: '#1d1e21', color: '#fff' } }
          );
          return;
        }
      }

      if (data.name) dispatch(addAnimationObject(data));
 
      setTimeout(() => {
        onObjectSelect?.(data);
      }, 0);

      onDrop && onDrop(data);
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    onDragOver && onDragOver(e);
  };

  const handleObjectSelect = (obj) => {
    onObjectSelect && onObjectSelect(obj);
  };

  const handleDeleteAnimationObject = (objectId) => {
    dispatch(removeAnimationObject(objectId));
    onDeleteAnimationObject && onDeleteAnimationObject(objectId);
  };

  const toggleCheckbox = (id) => {
    const targetObject = animationObjects.find((obj) => obj.id === id);
    if (!targetObject) return;
    dispatch(
      updateAnimationObject({
        id,
        updates: {
          isActive: !(targetObject.isActive ?? true),
        },
      })
    );
  };

  return (
    <div className="w-60 bg-[#131418] border-r border-[#4D4D4D] flex flex-col">
      {/* Header */}
      <div className="h-10 bg-[#131418] border-b border-[#4D4D4D] flex items-center justify-between px-4 flex-shrink-0">
        <div
          className="text-[#EE2B2A] font-mono text-xs"
          ref={(el) => {
            if (el) window.animationLeftPanelTimer = el;
          }}
        >
          {(() => {
            const time = currentTimeIndicator?.time ?? 0;
            const validTime = isNaN(time) ? 0 : time;

            return (
              <>
                {Math.floor(validTime / 60)
                  .toString()
                  .padStart(2, '0')}
                :
                {Math.floor(validTime % 60)
                  .toString()
                  .padStart(2, '0')}
                :
                {Math.floor((validTime % 1) * 100)
                  .toString()
                  .padStart(2, '0')}
              </>
            );
          })()}
        </div>

        <button
          onClick={() => setIsMinimized(!isMinimized)}
          className="bg-[#2B2C2F] h-4 w-6 flex items-center justify-center rounded"
        >
          <img
            className="h-2 w-2"
            src={isMinimized ? animationIcons?.Ani12 : animationIcons?.Ani11}
            alt={isMinimized ? 'Expand' : 'Collapse'}
          />
        </button>
      </div>

      {/* Title */}
      <div className="h-8 border-b border-[#4D4D4D] flex items-center px-4 flex-shrink-0">
        <h3 className="text-xs font-medium text-white">
          Animation Objects
        </h3>
      </div>

      {/* Object List */}
      <div
        className="flex-1 mt-2 overflow-y-auto text-xs"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#444447 #1a1b1e',
        }}
        ref={(el) => {
          if (el) window.animationLeftPanelScroll = el;
        }}
        onScroll={(e) => {
          if (window.animationTimelineScroll) {
            window.animationTimelineScroll.scrollTop = e.target.scrollTop;
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div>
          {animationObjects.map((obj) => {
            const isInactive = obj.isActive === false;
            const objectEffects =
              timelineEffects?.filter(
                (effect) => effect.objectId === obj.id
              ) || [];

            const usedRows = [
              ...new Set(objectEffects.map((effect) => effect.row || 0)),
            ].sort((a, b) => a - b);

            const rowCount = usedRows.length > 0 ? usedRows.length : 0;
            const objectHeight = Math.max(40, rowCount * 28 + 12);

            return (
              <div key={obj.id} className="mt-1">
                <div
                  className={`flex items-center px-2 w-full cursor-pointer ${isInactive
                    ? 'bg-[#1B1C20] opacity-60'
                    : selectedObject === obj.id
                      ? 'bg-[#2B2C2F]'
                      : 'bg-transparent'
                    }`}
                  style={{
                    height: `${objectHeight}px`,
                    minHeight: '40px',
                    transition: 'height 0.2s ease',
                  }}
                  onClick={() => handleObjectSelect(obj)}
                >
                  {/* Checkbox — never keep focus after toggle (avoids Ctrl+S browser Save dialog) */}
                  <input
                    type="checkbox"
                    checked={obj.isActive ?? true}
                    onMouseDown={(e) => {
                      // Prevent the checkbox from taking focus on click.
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleCheckbox(obj.id);
                      e.currentTarget.blur();
                    }}
                    className="mr-2 w-3.5 h-3.5 accent-[#EE2B2A] cursor-pointer focus:outline-none"
                  />

                  {/* Icon */}
                  <img
                    className={`w-4 h-4 mr-2 object-contain ${isInactive ? 'grayscale' : ''
                      }`}
                    src={obj.icon}
                    alt={obj.name}
                  />

                  {/* Name */}
                  <span
                    className={`text-xs flex-1 truncate ${isInactive ? 'text-gray-400' : 'text-white'
                      }`}
                  >
                    {obj.name}
                  </span>

                  {/* Delete */}
                  <img
                    className="w-[10px] h-3 cursor-pointer hover:opacity-75 transition-opacity"
                    src={animationIcons?.Ani16}
                    alt="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAnimationObject(obj.id);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AnimationLeftPanel;